import type { CharacterDto } from '@studio/shared';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { useCharacters } from '@/lib/queries';
import { CharacterDialog } from './CharacterDialog';
import { CharacterFolders, useCharacterFolders } from './CharacterFolders';
import { Button } from './ui/button';
import { Input } from './ui/form';
import { ConfirmDialog } from './ui/controls';
import { ErrorAlert } from './ui/misc';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

export function CharacterPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const q = useCharacters();
  const folders = useCharacterFolders();
  const qc = useQueryClient();
  const [folderId, setFolderId] = useState('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<CharacterDto | null>(null);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<CharacterDto | null>(null);
  useEffect(() => {
    if (q.data) {
      const valid = value.filter((id) => q.data.some((c) => c.id === id));
      if (valid.length !== value.length) onChange(valid);
    }
  }, [q.data, value, onChange]);
  const refresh = async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['characters'] }), qc.invalidateQueries({ queryKey: ['project'] })]); };
  const rename = useMutation({
    mutationFn: () => api(`/api/characters/${renaming!.id}`, { method: 'PATCH', body: { name: name.trim() } }),
    onSuccess: async () => { await refresh(); setRenaming(null); toast.success('Character renamed'); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/characters/${deleting!.id}`, { method: 'DELETE' }),
    onSuccess: async () => { onChange(value.filter((id) => id !== deleting?.id)); await refresh(); setDeleting(null); toast.success('Character deleted'); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const matches = q.data?.filter((c) => (!folderId || c.folderId === folderId) && `${c.name} ${c.callName}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())) ?? [];
  return <div className="space-y-3 rounded-lg border p-3">
    <CharacterFolders folderId={folderId} onChange={setFolderId} />
    <Input aria-label="Search characters" placeholder="Search characters…" value={search} onChange={(e) => setSearch(e.target.value)} />
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground">{value.length} selected</span>
      <Button type="button" size="sm" variant="outline" disabled={!folderId} onClick={() => setAdding(true)}><Plus /> New character</Button>
    </div>
    {!folderId && <p className="text-xs text-muted-foreground">Choose or create a folder to add a character. Search looks across all folders.</p>}
    {q.isLoading && <p className="text-sm">Loading characters…</p>}
    {q.error && <ErrorAlert message={errorMessage(q.error)} />}
    <div className="max-h-72 space-y-3 overflow-y-auto overscroll-contain">
      {folders.data?.filter((f) => !folderId || f.id === folderId).map((f) => {
        const items = matches.filter((c) => c.folderId === f.id);
        if (!items.length) return null;
        return <section key={f.id} className="space-y-1">
          <h4 className="break-words text-xs font-semibold text-muted-foreground">{f.title}</h4>
          {items.map((c) => <div key={c.id} className="flex items-center gap-1 rounded-md border p-1">
            <button type="button" aria-pressed={value.includes(c.id)} className={`flex min-w-0 flex-1 items-center gap-2 rounded p-1 text-left text-sm ${value.includes(c.id) ? 'bg-primary/10 text-primary ring-1 ring-primary' : 'hover:bg-accent'}`} onClick={() => onChange(value.includes(c.id) ? value.filter((id) => id !== c.id) : [...value, c.id])}>
              {c.images[0] ? <img src={c.images[0].url} alt="" className="size-10 shrink-0 rounded object-cover" /> : <UserRound className="size-10 shrink-0 rounded bg-muted p-2" />}
              <span className="break-words">{c.name}</span>
            </button>
            <Button type="button" variant="ghost" size="icon" aria-label={`Rename ${c.name}`} onClick={() => { setRenaming(c); setName(c.name); }}><Pencil /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${c.name}`} onClick={() => setDeleting(c)}><Trash2 /></Button>
          </div>)}
        </section>;
      })}
      {!q.isLoading && !q.error && !matches.length && <p className="py-3 text-sm text-muted-foreground">{search ? 'No matching characters.' : 'No characters in this folder yet.'}</p>}
    </div>
    {folderId && <CharacterDialog character={null} folderId={folderId} open={adding} onOpenChange={setAdding} />}
    <Dialog open={!!renaming} onOpenChange={(v) => !v && setRenaming(null)}><DialogContent>
      <DialogHeader><DialogTitle>Rename character</DialogTitle><DialogDescription>This updates the character name across the library and project cast. Existing script text is unchanged.</DialogDescription></DialogHeader>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if(name.trim() && !rename.isPending) rename.mutate(); }}>
        <Input aria-label="Character name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        <DialogFooter><Button type="submit" disabled={!name.trim()} loading={rename.isPending}>Save name</Button></DialogFooter>
      </form>
    </DialogContent></Dialog>
    <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)} title={`Delete ${deleting?.name}?`} description="This deletes the character from the library and removes it from every project cast. Existing script text stays unchanged." confirmLabel="Delete character" destructive loading={del.isPending} onConfirm={() => del.mutate()} />
  </div>;
}
